'use client';

import {
  RampAdUnit,
  RampRuntime,
  RampSpaAdsOptions,
} from '@/features/ads/shared/types/playwire-runtime';

export type { RampAdUnit, RampRuntime, RampSpaAdsOptions };

const RAMP_SCRIPT_ID = 'playwire-ramp';

type RampIds = { pubId: string; websiteId: string };

function getRampIds(): RampIds | null {
  const pubId = process.env.NEXT_PUBLIC_RAMP_PUB_ID;
  const websiteId = process.env.NEXT_PUBLIC_RAMP_WEBSITE_ID;

  return pubId && websiteId ? { pubId, websiteId } : null;
}

export function getRamp(): RampRuntime | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.ramp ?? null;
}

export function ensureRampQueue(): RampRuntime {
  const ramp = (window.ramp ??= {});
  ramp.que ??= [];
  return ramp;
}

/**
 * Injects Playwire's ramp.js once. Ramp (with Confiant, GPT and prebid) is only loaded on
 * the first queued Ramp command, i.e. on pages that render ad slots, not on every page.
 * Safe to call repeatedly and across SPA navigations.
 */
export function loadRampScript(): boolean {
  const ids = getRampIds();

  if (typeof window === 'undefined' || !ids) {
    return false;
  }

  if (document.getElementById(RAMP_SCRIPT_ID)) {
    return true;
  }

  const ramp = ensureRampQueue();
  ramp.passiveMode = true;

  const script = document.createElement('script');
  script.id = RAMP_SCRIPT_ID;
  script.src = `https://cdn.intergient.com/${ids.pubId}/${ids.websiteId}/ramp.js`;
  document.body.appendChild(script);

  return true;
}

export function queueRampCommand(command: (ramp: RampRuntime) => void): boolean {
  if (typeof window === 'undefined' || !getRampIds()) {
    return false;
  }

  const ramp = ensureRampQueue();
  ramp.que?.push(() => {
    command(ramp);
  });
  loadRampScript();

  return true;
}

/**
 * Removes every Ramp unit, including sticky ones (bottom_rail, corner_ad_video, flex).
 * Pages without ad slots no longer call spaAds, so the page that owned the units has to
 * clean up when it unmounts. No-op when Ramp was never started on this page view.
 */
export function queueDestroyAllRampUnits(): void {
  getRamp()?.que?.push(() => {
    getRamp()?.destroyUnits?.('all');
  });
}

export function queueSpaAds(options: RampSpaAdsOptions): boolean {
  return queueRampCommand((ramp) => {
    if (typeof ramp.spaAds !== 'function') {
      return;
    }

    ramp.spaAds(options);
  });
}

function runSpaAddAds(ramp: RampRuntime, ads: RampAdUnit[]): Promise<void> {
  return Promise.resolve().then(() => {
    if (typeof ramp.spaAddAds !== 'function') {
      throw new Error('Ramp spaAddAds is unavailable');
    }

    return ramp.spaAddAds(ads);
  });
}

export function queueSpaAddAds(ads: RampAdUnit[], ramp?: RampRuntime): Promise<void> {
  if (ramp) {
    return runSpaAddAds(ramp, ads);
  }

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Ramp queue is unavailable'));
  }

  return new Promise<void>((resolve, reject) => {
    const queued = queueRampCommand((queuedRamp) => {
      void runSpaAddAds(queuedRamp, ads).then(resolve, reject);
    });

    if (!queued) {
      reject(new Error('Ramp queue is unavailable'));
    }
  });
}
